'use client';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { LobbyGridPage } from '@/hooks/use-lobby-grid-page';
import { gridIdToPath } from '@/lib/utils';
import {
  mockRewardEvents,
  mockSaleEvents,
  mockSeats,
} from '@/features/lobby/data/mock-rewards';
import type {
  V2GridRewardEvent,
  V2GridSeatEvent,
} from '@/lib/types/v2';
import type { SeatData } from '@/components/grid';
import { resolveLobbyLiveGridId } from '@/features/lobby/data/boardroom-grid';
import { useTutorialOptional } from '@/features/tutorial/TutorialContext';
import { TUTORIAL_BOARD_ID } from '@/features/tutorial/tutorial-board';

export interface UseLobbyRewardsOpts {
  boardId: string;
  /** Number of taken seats — only used by the mock seat generator so
   *  the listings histogram has a sensible density on non-live boards. */
  taken: number;
  /** Live seat array from `useLobbySeats`. When the board is live we
   *  derive Listings from these (matches what the canvas is showing).
   *  Mock boards build their own listings via `mockSeats(boardId)`. */
  liveSeats?: SeatData[];
  /** Live grid page result hoisted from `useLobbyGridPage(activeBoardId)`
   *  in the parent so every right-column hook (seats / adapters /
   *  rewards) shares one fetch + one SSE stream. */
  gridPage: LobbyGridPage;
}

export interface UseLobbyRewardsResult {
  rewardEvents: V2GridRewardEvent[];
  saleEvents: V2GridSeatEvent[];
  /** Seat snapshot used by the Listings tab. For live boards this is
   *  the same array `useLobbySeats` returns; for mock boards it's a
   *  deterministic generator keyed off the board id. */
  listingsSeats: SeatData[];
  isLoading: boolean;
  isLive: boolean;
}

/**
 * Resolve the three time-series powering the Rewards|Listings|Sales
 * tabbed panel for the active lobby board. Live grids pull from the
 * same `useGridPage` query the seats / floor / activity surfaces use
 * (so a single network round-trip feeds the whole right column). Non-
 * live boards fall back to the deterministic mock generators so dev
 * mocks and the boardroom snapshot keep rendering populated charts.
 *
 * Sale events on live grids are filtered to the seat-trade subset
 * (vacant claim / buyout / yield-claim / abandoned) so the Sales tab
 * doesn't get drowned in deposit / price-update noise.
 */
const LIVE_SALE_TYPES = new Set<V2GridSeatEvent['eventType']>([
  'BOUGHT_VACANT',
  'BOUGHT_FROM_CREATOR',
  'BOUGHT_OUT',
  'YIELD_CLAIMED',
]);

/** Same row shape as `/api/grid/.../events-chart` (see grid detail page). */
type GridEventsChartRow = {
  id: string;
  eventType: string;
  user: string;
  timestamp: number;
  price: string | null;
  newPrice: string | null;
  oldHolder: string | null;
  seatId: string;
};

function chartRowsToSeatEvents(
  gridId: string,
  rows: GridEventsChartRow[],
): V2GridSeatEvent[] {
  return rows.map((e) => ({
    id: e.id,
    seatId: e.seatId,
    gridId,
    eventType: e.eventType as V2GridSeatEvent['eventType'],
    user: e.user as `0x${string}`,
    timestamp: e.timestamp,
    blockNumber: 0,
    txHash: '',
    price: e.price ?? undefined,
    newPrice: e.newPrice ?? undefined,
    oldHolder: (e.oldHolder ?? undefined) as `0x${string}` | undefined,
  }));
}

export function useLobbyRewards(opts: UseLobbyRewardsOpts): UseLobbyRewardsResult {
  const { boardId, taken, liveSeats, gridPage } = opts;
  const tutorial = useTutorialOptional();
  const liveId = resolveLobbyLiveGridId(boardId);
  const {
    rewardEventsUsd,
    recentEvents,
    gridLoading,
  } = gridPage;

  const { data: fullEventsData } = useQuery({
    queryKey: ['grid-events-chart', liveId],
    queryFn: async (): Promise<GridEventsChartRow[] | null> => {
      const res = await fetch(`/api/grid/${gridIdToPath(liveId!)}/events-chart?window=all`);
      if (!res.ok) return null;
      const json = await res.json();
      // Runtime guard: a stale L2 (Redis) cache entry from a prior
      // deploy can otherwise reach this consumer as a wrapper object
      // (`{ events: [...], fetchedAt }`) instead of the raw array, and
      // `chartRowsToSeatEvents` then crashes on `.map()` — exactly the
      // homepage crash on commit 4f0a710. Fail closed at the wire.
      const events = json?.data?.events;
      return Array.isArray(events) ? (events as GridEventsChartRow[]) : null;
    },
    enabled: !!liveId,
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  return useMemo<UseLobbyRewardsResult>(() => {
    if (tutorial && boardId === TUTORIAL_BOARD_ID) {
      return {
        rewardEvents: tutorial.state.rewardEvents,
        saleEvents: [],
        listingsSeats: tutorial.state.seats,
        isLoading: false,
        isLive: false,
      };
    }
    if (liveId) {
      // Mirror single-grid `page.tsx`: full history from events-chart; fall back
      // to `recentEvents` from the grid payload while the chart query loads or
      // if the request fails (same as Sales tab feed).
      const seatEventSource: V2GridSeatEvent[] =
        fullEventsData != null
          ? chartRowsToSeatEvents(liveId, fullEventsData)
          : recentEvents;

      const saleEvents = seatEventSource.filter((e) =>
        LIVE_SALE_TYPES.has(e.eventType),
      );
      return {
        rewardEvents: rewardEventsUsd,
        saleEvents,
        listingsSeats: liveSeats ?? [],
        isLoading: gridLoading,
        isLive: true,
      };
    }
    return {
      rewardEvents: mockRewardEvents(boardId, boardId),
      saleEvents: mockSaleEvents(boardId, boardId),
      listingsSeats: mockSeats(boardId, taken),
      isLoading: false,
      isLive: false,
    };
  }, [
    tutorial,
    liveId,
    fullEventsData,
    rewardEventsUsd,
    recentEvents,
    gridLoading,
    boardId,
    taken,
    liveSeats,
  ]);
}
